import React from 'react';
import { motion } from 'framer-motion';
import { Check, Shield, Lock, Star, Quote, CreditCard, Banknote, Smartphone } from 'lucide-react';
import { Button } from './ui/Button';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { TrustSection } from './TrustSection';
export function PurchaseSection() {
  const {
    trackInitiateCheckout
  } = useMetaPixel();
  const combos = [{
    title: 'Guía para Hacer Esquejes',
    subtitle: 'Todo lo que necesitas para empezar',
    price: '$13.99',
    priceValue: 13.99,
    currency: 'USD',
    image: 'https://cdn.magicpatterns.com/uploads/veKGvWWMpGDJWAzPky54mN/ChatGPT_Image_19_ene_2026,_03_20_12.png',
    features: ['Guía Completa para Hacer Esquejes', 'Bonus 1: Sustratos Caseros', 'Bonus 2: Guía Rápida de Plantas Populares', 'Bonus 3: Hormonas de Enraizamiento', 'Bonus 4: Mini Invernadero Casero', 'Bonus 5: Calendario del Esqueje', 'Entrega inmediata en PDF', 'Acceso de por vida'],
    cta: 'Empezar ahora',
    checkoutUrl: 'https://pay.hotmart.com/V104246835H',
    highlight: false
  }, {
    title: 'Propagación Completa',
    subtitle: 'Ideal si quieres profundizar',
    price: '$19.90',
    priceValue: 19.9,
    currency: 'USD',
    image: 'https://cdn.magicpatterns.com/uploads/ffJZeiPw7ZJZT5U4bDmMkG/Gula_practica_para_plantAs_sanas,_FUERTES_y_sin_venenos..png',
    features: ['Todo lo incluido en el Combo 1', 'Manual: Fertilizantes y Plaguicidas Naturales Caseros', 'Entrega inmediata en PDF', 'Acceso de por vida'],
    cta: 'Obtener Combo Completo',
    checkoutUrl: 'https://pay.hotmart.com/L104247048Q',
    highlight: true,
    badge: 'RECOMENDADO'
  }, {
    title: 'Producción Comercial',
    subtitle: 'Empieza a vender tus propias plantas',
    price: '$26.90',
    priceValue: 26.9,
    currency: 'USD',
    image: 'https://cdn.magicpatterns.com/uploads/adiwPphaDGshDTcc65KzCi/Guia_practica_para_plantas_sanas_fuertes_y_sin_venenos.png',
    features: ['Todo lo incluido en el Combo 1', 'Todo lo incluido en el Combo 2', 'Manual: Organización y Producción a Pequeña Escala', 'Entrega inmediata en PDF', 'Acceso de por vida'],
    cta: 'Quiero Vender Plantas',
    checkoutUrl: 'https://pay.hotmart.com/T104247116J',
    highlight: false
  }];
  const handleCheckoutClick = (combo: (typeof combos)[0]) => {
    trackInitiateCheckout(combo.priceValue, combo.title);
  };
  const testimonials = [{
    text: 'Finalmente entendí por qué mis esquejes no prosperaban. Ahora tengo plantas para regalar.',
    author: 'Martín',
    location: 'Bogotá, Colombia'
  }, {
    text: 'Pensé que era difícil, pero la guía lo hace súper simple. Hasta mi mamá lo está usando.',
    author: 'Lucía',
    location: 'Buenos Aires, Argentina'
  }, {
    text: 'Dejé de perder tiempo buscando en YouTube. Aquí está todo lo que necesito.',
    author: 'Diego',
    location: 'Ciudad de México, México'
  }, {
    text: 'Lo mejor es que explica todo pensando en nuestro clima. Los consejos funcionan de verdad acá.',
    author: 'Camila',
    location: 'Santiago, Chile'
  }, {
    text: 'Pagué sin problema y en 2 minutos ya tenía todo en mi correo.',
    author: 'Andrés',
    location: 'Lima, Perú'
  }, {
    text: 'Mis amigas no podían creer que yo hice todos esos esquejes. Ya les pasé el dato.',
    author: 'Valentina',
    location: 'Montevideo, Uruguay'
  }];
  return <section id="combos" className="py-20 bg-[#fafaf9]" data-id="element-135">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-136">
        {/* Header & Social Proof */}
        <div className="max-w-4xl mx-auto text-center mb-12" data-id="element-137">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-138">
            Elige el combo que mejor se adapte a tu objetivo
          </h2>
          <p className="text-xl text-gray-600 mb-8" data-id="element-139">
            Todos los materiales son digitales y de acceso inmediato
          </p>
        </div>

        {/* Local Payment Methods */}
        <div className="max-w-4xl mx-auto mb-12" data-id="element-140">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-141">
            <div className="text-center mb-5" data-id="element-142">
              <div className="flex items-center justify-center gap-2 mb-2" data-id="element-143">
                <CreditCard className="w-5 h-5 text-[#4a7c2c]" data-id="element-144" />
                <span className="font-bold text-gray-900" data-id="element-145">
                  Paga en tu moneda local
                </span>
              </div>
              <p className="text-sm text-gray-500" data-id="element-146">
                Al momento del pago, elige tu país y verás el precio convertido
                a tu moneda con los medios de pago disponibles
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6" data-id="element-147">
              <div data-id="element-148">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 text-center" data-id="element-149">
                  🇨🇴 Colombia
                </p>
                <img src="https://cdn.magicpatterns.com/uploads/mjVJJdnmKbztQhKmGf5aaz/image.png" alt="Medios de pago Colombia: Bancolombia, Débito/Crédito, Efecty, Nequi, PSE, PayPal" className="w-full rounded-lg border border-gray-200" data-id="element-150" />
              </div>
              <div data-id="element-151">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 text-center" data-id="element-152">
                  🇨🇱 Chile
                </p>
                <img src="https://cdn.magicpatterns.com/uploads/exXA4XzGxYdErN9cf4TW69/pasted-image.png" alt="Medios de pago Chile: MACH, PayPal, Dos tarjetas, ServiPag, Débito/Crédito, Sencillito" className="w-full rounded-lg border border-gray-200" data-id="element-153" />
              </div>
            </div>
          </div>
        </div>

        {/* Secure Payment Badge - Before Combos */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-600 bg-white p-4 rounded-lg shadow-sm border border-gray-100 max-w-md mx-auto mb-12" data-id="element-154">
          <div className="flex items-center gap-2" data-id="element-155">
            <Lock className="w-4 h-4 text-[#4a7c2c]" data-id="element-156" />
            <span data-id="element-157">Pago 100% seguro procesado por:</span>
          </div>
          <img src="https://cdn.magicpatterns.com/uploads/k9x8JCcUwfWxsaEMNBiB2R/Hotmart_logo.svg.png" alt="Hotmart" className="h-6 object-contain" data-id="element-158" />
        </div>

        {/* Combos Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16 items-start" data-id="element-159">
          {combos.map((combo, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className={`relative bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col ${combo.highlight ? 'ring-4 ring-[#4a7c2c] scale-105 z-10' : 'border border-gray-100'}`} data-id="element-160">
              {combo.highlight && <div className="bg-[#4a7c2c] text-white text-center py-2 font-bold text-sm tracking-wide" data-id="element-161">
                  {combo.badge}
                </div>}

              <div className="p-6 flex-1 flex flex-col" data-id="element-162">
                <div className="aspect-[4/3] mb-6 bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center p-4" data-id="element-163">
                  <img src={combo.image} alt={combo.title} className="h-full w-auto object-contain hover:scale-105 transition-transform duration-300" data-id="element-164" />
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-165">
                  {combo.title}
                </h3>
                <p className="text-sm text-gray-500 mb-4 h-10" data-id="element-166">
                  {combo.subtitle}
                </p>

                <ul className="space-y-3 mb-6 flex-1" data-id="element-167">
                  {combo.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3 text-sm text-gray-700" data-id="element-168">
                      <Check className="w-4 h-4 text-[#6b9d3e] flex-shrink-0 mt-0.5" data-id="element-169" />
                      <span data-id="element-170">{feature}</span>
                    </li>)}
                </ul>

                <div className="text-3xl font-bold text-[#4a7c2c] mb-1" data-id="element-171">
                  {combo.price}{' '}
                  <span className="text-sm font-normal text-gray-500" data-id="element-172">
                    {combo.currency}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mb-5" data-id="element-173">
                  Puedes pagar en tu moneda local eligiendo el país
                </p>

                <a href={combo.checkoutUrl} target="_blank" rel="noopener noreferrer" className="w-full" onClick={() => handleCheckoutClick(combo)} data-id="element-174">
                  <Button variant={combo.highlight ? 'primary' : 'outline'} fullWidth className={combo.highlight ? 'py-4 text-lg' : ''} data-id="element-175">
                    {combo.cta}
                  </Button>
                </a>

                <div className="mt-4 text-[10px] text-center text-gray-400 flex items-center justify-center gap-2" data-id="element-176">
                  <Lock className="w-3 h-3" data-id="element-177" />
                  Pago seguro · Acceso inmediato · 7 días de garantía
                </div>
              </div>

              {i === 0 && <div className="bg-gray-50 p-4 text-xs text-gray-500 border-t border-gray-100" data-id="element-178">
                  <div className="flex items-center gap-2 mb-1" data-id="element-179">
                    <Check className="w-3 h-3 text-[#4a7c2c]" data-id="element-180" /> Resultados
                    desde el primer intento
                  </div>
                  <div className="flex items-center gap-2" data-id="element-181">
                    <Check className="w-3 h-3 text-[#4a7c2c]" data-id="element-182" /> Ideal para
                    principiantes
                  </div>
                </div>}
            </motion.div>)}
        </div>
      </div>

      {/* Trust Section - Before Guarantee */}
      <TrustSection data-id="element-183" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-184">
        {/* Security & Guarantee */}
        <div className="max-w-3xl mx-auto text-center space-y-8" data-id="element-185">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-600 bg-white p-4 rounded-lg shadow-sm border border-gray-100" data-id="element-186">
            <div className="flex items-center gap-2" data-id="element-187">
              <Lock className="w-4 h-4 text-[#4a7c2c]" data-id="element-188" />
              <span data-id="element-189">Pago 100% seguro procesado por:</span>
            </div>
            <img src="https://cdn.magicpatterns.com/uploads/k9x8JCcUwfWxsaEMNBiB2R/Hotmart_logo.svg.png" alt="Hotmart" className="h-6 object-contain" data-id="element-190" />
          </div>

          <div className="bg-[#4a7c2c]/5 p-8 rounded-2xl border-2 border-[#4a7c2c]/10 relative overflow-hidden" data-id="element-191">
            <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-192">
              <Shield className="w-32 h-32 text-[#4a7c2c]" data-id="element-193" />
            </div>

            <div className="relative z-10" data-id="element-194">
              <h4 className="text-2xl font-bold text-[#2d5016] mb-4 flex items-center justify-center gap-2" data-id="element-195">
                <Shield className="w-8 h-8" data-id="element-196" />
                Garantía total de 7 días
              </h4>
              <p className="text-lg text-gray-700 mb-4 max-w-2xl mx-auto" data-id="element-197">
                Compra tranquilo. Si tienes algún inconveniente con el producto,
                tienes 7 días para solicitar tu reembolso completo a través de
                Hotmart. Sin preguntas, sin complicaciones.
              </p>
              <p className="text-sm font-medium text-[#4a7c2c]" data-id="element-198">
                Más de 1.500 personas ya confiaron en esta guía. Tu compra está
                protegida.
              </p>
            </div>
          </div>

          <p className="text-xs text-gray-400 max-w-2xl mx-auto" data-id="element-199">
            💡 Importante: Puedes pagar con tarjeta de crédito, débito,
            transferencia bancaria, billeteras digitales o efectivo en puntos
            autorizados según tu país. Tu compra es procesada de forma segura
            por Hotmart.
          </p>
        </div>

        {/* Testimonials - After Checkout & Guarantee */}
        <div className="max-w-6xl mx-auto mt-20" data-id="element-200">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-12" data-id="element-201">
            Lo que dicen quienes ya tienen la guía
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-202">
            {testimonials.map((t, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 relative" data-id="element-203">
                <Quote className="absolute top-6 right-6 w-8 h-8 text-[#4a7c2c]/10" data-id="element-204" />
                <div className="flex gap-1 mb-4" data-id="element-205">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-206" />)}
                </div>
                <p className="text-gray-700 mb-6 italic relative z-10" data-id="element-207">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-3" data-id="element-208">
                  <div className="w-10 h-10 rounded-full bg-[#4a7c2c]/10 flex items-center justify-center text-[#4a7c2c] font-bold" data-id="element-209">
                    {t.author[0]}
                  </div>
                  <div data-id="element-210">
                    <p className="font-bold text-gray-900" data-id="element-211">{t.author}</p>
                    <p className="text-sm text-gray-500" data-id="element-212">{t.location}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}